import { useState } from 'react';
import { FileDown, Loader2, Check, Mail } from 'lucide-react';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '@/components/ui/dialog';
import { Checkbox } from '@/components/ui/checkbox';
import { Label } from '@/components/ui/label';
import { Input } from '@/components/ui/input';
import { toast } from 'sonner';
import jsPDF from 'jspdf';
import {
  DATA_REFERENCIA,
  getKpisResumo,
  getKpisRecuperacao,
  getKpisDevedores,
  getAgingOpen,
  getAgingOverdue,
  getTopDevedores,
  getStepsStats,
  getReguasPerformance,
  getFunnelByRegua,
} from '@/data/dashboardMockData';

type TabKey = 'resumo' | 'recuperacao' | 'regua' | 'devedores';

interface TabOption {
  key: TabKey;
  label: string;
  checked: boolean;
}

function formatMoney(value: number): string {
  return new Intl.NumberFormat('pt-BR', {
    style: 'currency',
    currency: 'BRL',
    minimumFractionDigits: 0,
  }).format(value);
}

export function PDFExportButton() {
  const [isOpen, setIsOpen] = useState(false);
  const [isExporting, setIsExporting] = useState(false);
  const [showEmailInput, setShowEmailInput] = useState(false);
  const [emailAddress, setEmailAddress] = useState('');
  const [isSendingEmail, setIsSendingEmail] = useState(false);
  const [tabs, setTabs] = useState<TabOption[]>([
    { key: 'resumo', label: 'Resumo', checked: true },
    { key: 'recuperacao', label: 'Recuperação', checked: true },
    { key: 'regua', label: 'Régua de Cobrança', checked: true },
    { key: 'devedores', label: 'Devedores', checked: true },
  ]);

  const toggleTab = (key: TabKey) => {
    setTabs(tabs.map(t => t.key === key ? { ...t, checked: !t.checked } : t));
  };

  const handleSendEmail = async () => {
    if (!emailAddress) return;
    
    setIsSendingEmail(true);
    try {
      // Simulação de envio - em produção, aqui seria a integração com serviço de email
      await new Promise(resolve => setTimeout(resolve, 1500));
      toast.success(`Relatório enviado para ${emailAddress}`);
      setShowEmailInput(false);
      setEmailAddress('');
      setIsOpen(false);
    } catch (error) {
      toast.error('Erro ao enviar relatório por e-mail');
      console.error('Erro ao enviar email:', error);
    } finally {
      setIsSendingEmail(false);
    }
  };

  const generatePDF = async () => {
    setIsExporting(true);
    
    try {
      const doc = new jsPDF();
      let yPos = 20;
      const lineHeight = 7;
      const margin = 20;
      const pageWidth = doc.internal.pageSize.getWidth();

      const addTitle = (text: string, size = 16) => {
        doc.setFontSize(size);
        doc.setFont('helvetica', 'bold');
        doc.text(text, margin, yPos);
        yPos += lineHeight + 2;
      };

      const addSubtitle = (text: string) => {
        doc.setFontSize(12);
        doc.setFont('helvetica', 'bold');
        doc.text(text, margin, yPos);
        yPos += lineHeight;
      };

      const addText = (text: string) => {
        doc.setFontSize(10);
        doc.setFont('helvetica', 'normal');
        doc.text(text, margin, yPos);
        yPos += lineHeight;
      };

      const addLine = () => {
        doc.setDrawColor(200, 200, 200);
        doc.line(margin, yPos, pageWidth - margin, yPos);
        yPos += 5;
      };

      const checkNewPage = (needed = 40) => {
        if (yPos > doc.internal.pageSize.getHeight() - needed) {
          doc.addPage();
          yPos = 20;
        }
      };

      // Header
      addTitle('RELATÓRIO DE INDICADORES DE COBRANÇA', 18);
      addText(`Data: ${DATA_REFERENCIA}`);
      addText('Empresa: Digisend Cobranças');
      yPos += 5;
      addLine();

      // Aba Resumo
      if (tabs.find(t => t.key === 'resumo')?.checked) {
        checkNewPage();
        addTitle('RESUMO', 14);
        
        const kpis = getKpisResumo();
        addText(`• Saldo em aberto: ${formatMoney(kpis.saldoGeral)} (${kpis.saldoGeralFaturas} faturas)`);
        addText(`• A receber hoje: ${formatMoney(kpis.receberHoje)} (${kpis.receberHojeClientes} clientes)`);
        addText(`• Vencido +2 dias: ${formatMoney(kpis.vencido2d)} (${kpis.vencido2dClientes} clientes)`);
        yPos += 5;

        addSubtitle('Contas a Receber em Aberto:');
        const agingOpen = getAgingOpen();
        agingOpen.forEach(p => {
          addText(`  ${p.label}: ${formatMoney(p.valor)} (${p.clientes} clientes, ${p.faturas} faturas)`);
        });
        yPos += 5;
        addLine();
      }

      // Aba Recuperação
      if (tabs.find(t => t.key === 'recuperacao')?.checked) {
        checkNewPage();
        addTitle('RECUPERAÇÃO', 14);
        
        const kpis = getKpisRecuperacao();
        addText(`• Total recuperado: ${formatMoney(kpis.totalRecuperado)}`);
        addText(`• Taxa de recuperação: ${kpis.taxaRecuperacao}%`);
        addText(`• Melhor canal: ${kpis.melhorCanal}`);
        addText(`• Valor médio recuperado: ${formatMoney(kpis.valorMedioRecuperado)}`);
        yPos += 5;
        addLine();
      }

      // Aba Régua de Cobrança
      if (tabs.find(t => t.key === 'regua')?.checked) {
        checkNewPage();
        addTitle('RÉGUA DE COBRANÇA', 14);
        
        const steps = getStepsStats();
        addText(`• Steps ativos: SMS (${steps.sms}), Email (${steps.email}), WhatsApp (${steps.whatsapp})`);
        yPos += 3;

        addSubtitle('Performance por Régua:');
        const performance = getReguasPerformance();
        performance.forEach(r => {
          addText(`  ${r.ruleName}: ${r.paymentRate}% pagamento, ${r.engagementRate}% engajamento`);
        });
        yPos += 3;

        addSubtitle('Funil de Conversão (Todas as Réguas):');
        const funil = getFunnelByRegua('all');
        funil.forEach(step => {
          addText(`  ${step.name}: ${step.valor.toLocaleString('pt-BR')}`);
        });
        yPos += 5;
        addLine();
      }

      // Aba Devedores
      if (tabs.find(t => t.key === 'devedores')?.checked) {
        checkNewPage();
        addTitle('DEVEDORES', 14);
        
        const kpis = getKpisDevedores();
        addText(`• Total inadimplente: ${formatMoney(kpis.totalInadimplente)}`);
        addText(`• Clientes em atraso: ${kpis.clientesAtraso}`);
        addText(`• Ticket médio: ${formatMoney(kpis.ticketMedio)}`);
        addText(`• Dias médio de atraso: ${kpis.diasMedioAtraso} dias`);
        yPos += 3;

        addSubtitle('Top 5 Devedores:');
        const devedores = getTopDevedores();
        devedores.forEach((d, i) => {
          addText(`  ${i + 1}. ${d.name}: ${formatMoney(d.amount)} (${d.daysOverdue} dias)`);
        });
        yPos += 3;

        addSubtitle('Contas a Receber Vencidas:');
        const agingOverdue = getAgingOverdue();
        agingOverdue.forEach(p => {
          addText(`  ${p.label}: ${formatMoney(p.valor)} (${p.clientes} clientes)`);
        });
        const totalVencido = agingOverdue.reduce((sum, p) => sum + p.valor, 0);
        addText(`  TOTAL: ${formatMoney(totalVencido)}`);
      }

      // Footer
      const pageCount = doc.getNumberOfPages();
      for (let i = 1; i <= pageCount; i++) {
        doc.setPage(i);
        doc.setFontSize(8);
        doc.setFont('helvetica', 'normal');
        doc.text(
          `Página ${i} de ${pageCount} • Gerado em ${DATA_REFERENCIA}`,
          pageWidth / 2,
          doc.internal.pageSize.getHeight() - 10,
          { align: 'center' }
        );
      }

      // Save
      doc.save(`relatorio-indicadores-${new Date().toISOString().split('T')[0]}.pdf`);
      setIsOpen(false);
    } catch (error) {
      console.error('Erro ao gerar PDF:', error);
    } finally {
      setIsExporting(false);
    }
  };

  const selectedCount = tabs.filter(t => t.checked).length;

  return (
    <>
      <Button
        variant="outline"
        size="sm"
        onClick={() => setIsOpen(true)}
        className="gap-2"
      >
        <FileDown className="h-4 w-4" />
        Exportar PDF
      </Button>

      <Dialog open={isOpen} onOpenChange={setIsOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Exportar Relatório PDF</DialogTitle>
          </DialogHeader>

          <div className="py-4">
            <p className="text-sm text-muted-foreground mb-4">
              Selecione as seções que deseja incluir no relatório:
            </p>

            <div className="space-y-3">
              {tabs.map((tab) => (
                <div key={tab.key} className="flex items-center gap-3">
                  <Checkbox
                    id={tab.key}
                    checked={tab.checked}
                    onCheckedChange={() => toggleTab(tab.key)}
                  />
                  <Label htmlFor={tab.key} className="cursor-pointer">
                    {tab.label}
                  </Label>
                </div>
              ))}
            </div>
          </div>

          {/* Opção de enviar por e-mail */}
          <div className="border-t pt-4">
            <div className="flex items-center gap-3 mb-3">
              <Checkbox
                id="send-email"
                checked={showEmailInput}
                onCheckedChange={(checked) => setShowEmailInput(checked === true)}
              />
              <Label htmlFor="send-email" className="cursor-pointer flex items-center gap-2">
                <Mail className="h-4 w-4" />
                Enviar relatório por e-mail
              </Label>
            </div>
            
            {showEmailInput && (
              <Input
                type="email"
                placeholder="Digite o e-mail do destinatário"
                value={emailAddress}
                onChange={(e) => setEmailAddress(e.target.value)}
                className="mt-2"
              />
            )}
          </div>

          <DialogFooter className="flex-col sm:flex-row gap-2">
            <Button variant="outline" onClick={() => setIsOpen(false)}>
              Cancelar
            </Button>
            
            {showEmailInput && emailAddress && (
              <Button
                variant="secondary"
                onClick={handleSendEmail}
                disabled={selectedCount === 0 || isSendingEmail || !emailAddress}
                className="gap-2"
              >
                {isSendingEmail ? (
                  <>
                    <Loader2 className="h-4 w-4 animate-spin" />
                    Enviando...
                  </>
                ) : (
                  <>
                    <Mail className="h-4 w-4" />
                    Enviar por E-mail
                  </>
                )}
              </Button>
            )}
            
            <Button
              onClick={generatePDF}
              disabled={selectedCount === 0 || isExporting}
              className="gap-2"
            >
              {isExporting ? (
                <>
                  <Loader2 className="h-4 w-4 animate-spin" />
                  Gerando...
                </>
              ) : (
                <>
                  <Check className="h-4 w-4" />
                  Gerar PDF ({selectedCount})
                </>
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}
